/**
 * Which product this is, per market.
 *
 * Not the agency's brand. Three different things wear the word "brand" in
 * this codebase and conflating them is how a customer's logo ends up on
 * somebody else's invoice:
 *
 *   - `lib/brand.jsx`      the TENANT's brand: their name, logo, areas, colours.
 *   - `lib/crmBrand.js`    the TENANT's accent colour, as CSS channels.
 *   - this file            OUR product's name, for the chrome around their data.
 *
 * The last one is the one that was missing, so every CRM screen said
 * "Foundation CRM" in its eyebrow and its document title, including on the US
 * host where the product is called Statehouse. Thirty-eight literals across
 * twenty-seven files, each individually too small to notice and collectively
 * the reason the demo reads as a UK product with American data pasted in.
 *
 * Everything here is per MARKET, not per tenant. A US brokerage and a UK
 * agency are using two differently named products; two US brokerages are not.
 */
import { activeMarket } from "@/lib/marketBoot";

/**
 * @typedef {object} ProductBrand
 * @property {string} name        Full product name, for prose and breadcrumbs.
 * @property {string} shortName   What goes in a document title or a tight rail.
 * @property {string} domain      Marketing site, without scheme.
 * @property {string} supportEmail
 * @property {string} pricingUrl  Where "See plans" goes.
 * @property {string} locale      BCP-47, drives dates and number grouping.
 * @property {string} currency    ISO 4217.
 * @property {string} tz          IANA zone for rendering stored UTC.
 */

/** @type {Record<string, ProductBrand>} */
export const BRANDS = {
  uk: {
    name: "Foundation",
    shortName: "Foundation CRM",
    domain: "foundationagent.co.uk",
    supportEmail: "hello@foundationagent.co.uk",
    pricingUrl: "https://foundationagent.co.uk/#pricing",
    locale: "en-GB",
    currency: "GBP",
    // Every UK tenant is on one zone, and the standing rule for this codebase
    // is store UTC, render Europe/London.
    tz: "Europe/London",
  },
  us: {
    name: "Statehouse",
    // Deliberately not "Statehouse CRM". The UK short name carries "CRM"
    // because "Foundation" alone is a common noun and reads as a sentence
    // fragment in a tab; "Statehouse" does not have that problem.
    shortName: "Statehouse",
    domain: "statehousehq.com",
    supportEmail: "hello@statehousehq.com",
    pricingUrl: "https://statehousehq.com/#pricing",
    locale: "en-US",
    currency: "USD",
    // A placeholder that is wrong for most tenants and right for none of
    // them by luck: the US spans six zones, so anything rendered against
    // this is rendered against the brokerage's own zone when we know it
    // (see `zoneFor` in lib/datetime.js) and against this only as a floor.
    tz: "America/Chicago",
  },
};

/**
 * The product brand for a market. Unknown markets get UK, matching every
 * other default in this codebase, because a wrong-but-familiar brand is
 * recoverable and an undefined one throws in a render.
 *
 * @param {string} m
 * @returns {ProductBrand}
 */
export function productBrand(m) {
  return BRANDS[m === "us" ? "us" : "uk"];
}

/**
 * The product for whatever market the CRM is rendering right now.
 *
 * A function, not a constant: the market is narrowed during CrmLayout's
 * render, and a constant computed at import time would freeze every page on
 * the boot guess even after the brand lookup corrected it.
 *
 * Call it inside render (`{activeProduct().shortName}`), never at module
 * scope.
 *
 * @returns {ProductBrand}
 */
export function activeProduct() {
  return productBrand(activeMarket());
}
